// Graphics for the "Balance Is More Than Standing Still" masterclass.
//
// Two pieces live here:
//   <BalanceGraphic name="..."/>  — the illustrated panels (the four balance
//                                   jobs, the stability dial map, the
//                                   specific-to-general spectrum bar).
//   <SciencePanel .../>           — the collapsible "the science" panels that
//                                   tuck the research underneath each section
//                                   so it is there for the curious without
//                                   crowding the main read.
//
// Everything is hand-drawn SVG so it scales clean on retina and prints fine.
// Animation is CSS keyframes injected once per graphic. Plain language, no
// em dashes, matches the dark masterclass theme.

import React, { useState } from "react";
import { ChevronDown, FlaskConical } from "lucide-react";
import { GOLD } from "../constants/theme";

const GREEN = "#34D399"; // stability, the part that holds together
const RED = "#F87171";   // contact, drift, the part that is off

// ---- shared bits -----------------------------------------------------------

function Card({ C, children, style }) {
  return (
    <div style={{
      background: C.cardGradient,
      border: `1px solid ${C.cardBorder}`,
      borderRadius: 14,
      overflow: "hidden",
      ...style,
    }}>
      {children}
    </div>
  );
}

// A small stick figure court line every figure stands on.
function Floor({ y = 150, from = 14, to = 146, color }) {
  return <line x1={from} y1={y} x2={to} y2={y} stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />;
}

// ---- the four jobs ---------------------------------------------------------
// Each job is a labelled stick-figure panel. The visual cue on each one maps
// to the screenshots: wide braced base, holding through a hit, off the base
// still firing, legs drift while the top stays quiet.

function JobFind({ C }) {
  const ink = C.text;
  return (
    <svg viewBox="0 0 160 170" style={{ width: "100%", height: "auto" }} role="img" aria-label="Finding balance: decelerating into a wide braced base">
      <style>{`
        @keyframes bg-ghost { 0%,100%{opacity:.05} 50%{opacity:.22} }
        @keyframes bg-bracket { 0%{transform:scaleX(.55);opacity:.35} 60%,100%{transform:scaleX(1);opacity:1} }
      `}</style>
      <Floor color={ink} />
      {/* fading motion ghosts trailing in from a hard drive */}
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(${18 + i * 16},0)`} stroke={ink} strokeWidth="2" fill="none" opacity="0.16" style={{ animation: `bg-ghost 2.2s ${i * 0.2}s ease-in-out infinite` }}>
          <circle cx="14" cy="44" r="7" />
          <line x1="14" y1="51" x2="14" y2="92" />
          <line x1="14" y1="92" x2="6" y2="120" />
          <line x1="14" y1="92" x2="22" y2="120" />
        </g>
      ))}
      {/* the arrival: strong, even base, weight gathered over the feet */}
      <g stroke={ink} strokeWidth="3" fill="none" strokeLinecap="round">
        <circle cx="94" cy="40" r="9" fill={ink} stroke="none" />
        <line x1="94" y1="49" x2="94" y2="92" />
        {/* arms gathered, ready */}
        <line x1="94" y1="62" x2="80" y2="74" />
        <line x1="94" y1="62" x2="108" y2="72" />
        {/* even, symmetric base, loaded both legs */}
        <line x1="94" y1="92" x2="78" y2="148" />
        <line x1="94" y1="92" x2="110" y2="148" />
      </g>
      {/* green stability bracket fills evenly under the feet */}
      <g style={{ transformOrigin: "94px 158px", animation: "bg-bracket 2.4s ease-out infinite" }}>
        <path d="M74 156 L74 162 L114 162 L114 156" stroke={GREEN} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

function JobHold({ C }) {
  const ink = C.text;
  return (
    <svg viewBox="0 0 160 170" style={{ width: "100%", height: "auto" }} role="img" aria-label="Holding balance: taking a hit and springing back, base stays planted">
      <style>{`
        @keyframes bg-hit { 0%,55%,100%{transform:translateX(0)} 65%{transform:translateX(7px)} 80%{transform:translateX(-2px)} }
        @keyframes bg-arrow { 0%,50%{transform:translateX(-10px);opacity:0} 60%{transform:translateX(0);opacity:1} 90%,100%{transform:translateX(0);opacity:0} }
      `}</style>
      <Floor color={ink} />
      {/* upper body wobbles on contact then returns; base does not move */}
      <g style={{ transformOrigin: "80px 95px", animation: "bg-hit 2.6s ease-in-out infinite" }}>
        <g stroke={ink} strokeWidth="3" fill="none" strokeLinecap="round">
          <circle cx="80" cy="40" r="9" fill={ink} stroke="none" />
          <line x1="80" y1="49" x2="80" y2="92" />
          {/* loaded into a shot */}
          <line x1="80" y1="60" x2="64" y2="48" />
          <line x1="80" y1="60" x2="96" y2="46" />
          <circle cx="100" cy="40" r="5" fill="none" stroke={GOLD} strokeWidth="2.5" />
        </g>
      </g>
      {/* planted base, stays put */}
      <g stroke={ink} strokeWidth="3" fill="none" strokeLinecap="round">
        <line x1="80" y1="92" x2="66" y2="148" />
        <line x1="80" y1="92" x2="96" y2="148" />
      </g>
      <path d="M60 150 L100 150" stroke={GREEN} strokeWidth="3" strokeLinecap="round" opacity="0.9" />
      {/* the hit */}
      <g style={{ transformOrigin: "40px 78px", animation: "bg-arrow 2.6s ease-in-out infinite" }}>
        <line x1="30" y1="78" x2="62" y2="78" stroke={RED} strokeWidth="4" strokeLinecap="round" />
        <path d="M54 71 L64 78 L54 85" stroke={RED} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

function JobShootOff({ C }) {
  const ink = C.text;
  return (
    <svg viewBox="0 0 160 170" style={{ width: "100%", height: "auto" }} role="img" aria-label="Shooting off balance: weight outside the base, still releasing">
      <style>{`
        @keyframes bg-com { 0%,100%{r:4;opacity:.7} 50%{r:6;opacity:1} }
        @keyframes bg-ball { 0%{transform:translate(0,0)} 100%{transform:translate(14px,-30px)} }
      `}</style>
      <Floor color={ink} />
      {/* dashed base on the floor */}
      <rect x="58" y="143" width="44" height="12" rx="3" fill="none" stroke={ink} strokeWidth="2" strokeDasharray="4 4" opacity="0.6" />
      {/* tilted figure, splayed legs, leaning past the base */}
      <g stroke={ink} strokeWidth="3" fill="none" strokeLinecap="round" transform="rotate(-14 92 100)">
        <circle cx="104" cy="36" r="9" fill={ink} stroke="none" />
        <line x1="104" y1="45" x2="92" y2="92" />
        {/* release arm up */}
        <line x1="100" y1="60" x2="118" y2="40" />
        <line x1="96" y1="62" x2="82" y2="74" />
        {/* splayed legs catching */}
        <line x1="92" y1="92" x2="74" y2="146" />
        <line x1="92" y1="92" x2="108" y2="148" />
      </g>
      {/* center of mass red dot, sitting OUTSIDE the dashed base */}
      <circle cx="118" cy="150" r="5" fill={RED} style={{ animation: "bg-com 1.8s ease-in-out infinite" }} />
      {/* ball releasing */}
      <circle cx="120" cy="34" r="6" fill="none" stroke={GOLD} strokeWidth="2.5" style={{ transformOrigin: "120px 34px", animation: "bg-ball 2.2s ease-out infinite" }} />
    </svg>
  );
}

function JobGiveUp({ C }) {
  const ink = C.text;
  return (
    <svg viewBox="0 0 160 170" style={{ width: "100%", height: "auto" }} role="img" aria-label="Giving up balance on purpose: legs drift, upper body stays quiet and square">
      <style>{`
        @keyframes bg-drift { 0%,100%{transform:translateX(-6px)} 50%{transform:translateX(10px)} }
        @keyframes bg-ring { 0%,100%{opacity:.45} 50%{opacity:1} }
      `}</style>
      <Floor color={ink} />
      {/* airborne, drifting laterally */}
      <g style={{ transformOrigin: "80px 90px", animation: "bg-drift 2.8s ease-in-out infinite" }}>
        {/* lower body drifts */}
        <g stroke={ink} strokeWidth="3" fill="none" strokeLinecap="round">
          <line x1="80" y1="62" x2="80" y2="96" />
          <line x1="80" y1="96" x2="60" y2="124" />
          <line x1="80" y1="96" x2="98" y2="126" />
        </g>
      </g>
      {/* quiet zone ring around the square, steady shoulders + arms */}
      <g>
        <ellipse cx="80" cy="48" rx="34" ry="30" fill="none" stroke={GREEN} strokeWidth="2" strokeDasharray="3 4" style={{ animation: "bg-ring 2.8s ease-in-out infinite" }} />
        <g stroke={ink} strokeWidth="3" fill="none" strokeLinecap="round">
          <circle cx="80" cy="34" r="9" fill={ink} stroke="none" />
          <line x1="80" y1="43" x2="80" y2="62" />
          {/* square shooting frame */}
          <line x1="80" y1="50" x2="64" y2="44" />
          <line x1="80" y1="50" x2="96" y2="44" />
          <circle cx="100" cy="40" r="5" fill="none" stroke={GOLD} strokeWidth="2.5" />
        </g>
      </g>
      {/* drift arrow under the feet */}
      <g>
        <line x1="50" y1="150" x2="112" y2="150" stroke={GREEN} strokeWidth="3" strokeLinecap="round" />
        <path d="M104 143 L114 150 L104 157" stroke={GREEN} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

const JOBS = [
  { key: "find", label: "A · Find balance", cue: "Arrive on a strong, even base", Fig: JobFind,
    sub: "Coming in fast off a drive or a catch, gather your weight back over your feet and load both legs evenly, so the force drives straight up into the shot instead of spilling past you." },
  { key: "hold", label: "B · Hold balance", cue: "Absorb it, spring back", Fig: JobHold,
    sub: "You are already balanced and something pulls at it: your own momentum, an awkward landing, a bump, a late contest. The base takes the force and springs right back." },
  { key: "shoot", label: "C · Shoot without it", cue: "Off the base, still firing", Fig: JobShootOff,
    sub: "Your weight is already past your base with no time to reset, so you launch from there anyway and still hold the shot together." },
  { key: "giveup", label: "D · Give it up on purpose", cue: "Let the legs go, keep the top quiet", Fig: JobGiveUp,
    sub: "You give up the floor on purpose, drifting or fading for space, while the upper body stays square and keeps its shooting platform." },
];

function BalanceJobs({ C }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, margin: "20px 0" }}>
      {JOBS.map(({ key, label, cue, sub, Fig }) => (
        <Card key={key} C={C} style={{ padding: "12px 12px 14px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: GOLD, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
          <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, padding: "4px 0" }}>
            <Fig C={C} />
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text, marginTop: 8 }}>{cue}</div>
          <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5, marginTop: 3 }}>{sub}</div>
        </Card>
      ))}
    </div>
  );
}

// ---- the stability dial map ------------------------------------------------
// A 2x2: on balance / off balance crossed with explore / anchor. The
// undertrained cell (search the arrival) is called out in gold.

const DIAL_CELLS = [
  { row: "On balance", col: "Explore", title: "Search the arrival", body: "Hunt different ways to land on balance off the catch and the dribble. The most undertrained cell.", star: true },
  { row: "On balance", col: "Anchor", title: "Groove the release", body: "Set, balanced reps that lock in the shot you already own." },
  { row: "Off balance", col: "Explore", title: "Search drift and float", body: "Play with fades, drifts, and recoveries to widen what you can launch from." },
  { row: "Off balance", col: "Anchor", title: "Force it, then repeat it", body: "Pick one hard off balance shot and rep it until it holds together on demand." },
];

// The threshold dial on its own, so it can sit right where the threshold is
// introduced rather than at the bottom of the section.
function BalanceThreshold({ C }) {
  return (
    <Card C={C} style={{ padding: "14px 16px", margin: "20px 0" }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: GOLD, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>The balance threshold</div>
      <div style={{ position: "relative", height: 14, borderRadius: 8, background: `linear-gradient(90deg, ${GREEN} 0%, ${GOLD} 50%, ${RED} 100%)`, opacity: 0.85 }}>
        <div style={{ position: "absolute", left: "50%", top: -4, bottom: -4, width: 2, background: C.text }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMid, marginTop: 8, lineHeight: 1.4 }}>
        <span style={{ maxWidth: "46%" }}>Below the line: on balance on most reps. Refine the shot.</span>
        <span style={{ maxWidth: "46%", textAlign: "right" }}>Above the line: off balance by default. Grind toward one that holds.</span>
      </div>
    </Card>
  );
}

// The 2x2 map (difficulty x intent) on its own.
function StabilityMap({ C }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: "20px 0" }}>
      {DIAL_CELLS.map((cell) => (
        <div key={cell.title} style={{
          padding: "14px 15px",
          borderRadius: 12,
          background: cell.star ? `${GOLD}14` : C.cardGradient,
          border: `1px solid ${cell.star ? GOLD : C.cardBorder}`,
          position: "relative",
        }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
            <Tag C={C}>{cell.row}</Tag>
            <Tag C={C}>{cell.col}</Tag>
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: cell.star ? GOLD : C.text, marginBottom: 5 }}>
            {cell.title}{cell.star ? " ★" : ""}
          </div>
          <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.55 }}>{cell.body}</div>
        </div>
      ))}
    </div>
  );
}

// Combo (threshold + map), kept for any caller that wants both at once.
function StabilityDial({ C }) {
  return (
    <div style={{ margin: "22px 0" }}>
      <BalanceThreshold C={C} />
      <StabilityMap C={C} />
    </div>
  );
}

function Tag({ C, children }) {
  return (
    <span style={{
      fontSize: 9.5, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase",
      padding: "2px 7px", borderRadius: 5, color: C.textMid,
      border: `1px solid ${C.cardBorder}`,
    }}>{children}</span>
  );
}

// ---- the specific to general spectrum bar ----------------------------------

function SpectrumBar({ C }) {
  const stops = [
    { pct: 6, label: "Most specific", note: "The exact shot, defender and all" },
    { pct: 50, label: "Middle", note: "Same qualities, new dressing" },
    { pct: 94, label: "Most general", note: "Raw braking, trunk, ankles, sense of where you are" },
  ];
  return (
    <Card C={C} style={{ padding: "18px 18px 16px", margin: "22px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 800, color: C.textMid, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>
        <span>More specific</span>
        <span>More general</span>
      </div>
      <div style={{ position: "relative", height: 16, borderRadius: 10, background: `linear-gradient(90deg, ${GOLD} 0%, ${C.cardBorder} 100%)` }}>
        {stops.map((s) => (
          <div key={s.label} style={{ position: "absolute", left: `${s.pct}%`, top: -3, transform: "translateX(-50%)" }}>
            <div style={{ width: 12, height: 22, borderRadius: 4, background: C.text, boxShadow: "0 0 0 3px rgba(0,0,0,0.35)" }} />
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 14 }}>
        {stops.map((s) => (
          <div key={s.label} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>{s.label}</div>
            <div style={{ fontSize: 11.5, color: C.textMid, lineHeight: 1.45, marginTop: 3 }}>{s.note}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const GRAPHICS = {
  "balance-jobs": BalanceJobs,
  "stability-dial": StabilityDial,
  "balance-threshold": BalanceThreshold,
  "stability-map": StabilityMap,
  "spectrum-bar": SpectrumBar,
};

export function BalanceGraphic({ name, caption, C }) {
  const G = GRAPHICS[name];
  if (!G) return null;
  return (
    <figure style={{ margin: "8px 0" }}>
      <G C={C} />
      {caption && (
        <figcaption style={{ fontSize: 12, color: C.textDim, fontStyle: "italic", lineHeight: 1.5, marginTop: 4, textAlign: "center" }}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// ---- collapsible science panel ---------------------------------------------
// Tucks the research underneath each section. Closed by default so the main
// read stays clean; opens for the curious. Accepts a body string and/or a
// list of {term, def} rows.

export function SciencePanel({ title, body, items, C }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ margin: "20px 0", border: `1px solid ${C.cardBorder}`, borderRadius: 12, overflow: "hidden", background: C.cardGradient }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 9,
          padding: "13px 15px", background: "transparent", border: "none",
          cursor: "pointer", textAlign: "left", color: C.text,
        }}
      >
        <FlaskConical size={15} color={GOLD} strokeWidth={2.5} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: GOLD }}>The science</span>
        {title && <span style={{ fontSize: 13, fontWeight: 700, color: C.textMid }}>· {title}</span>}
        <ChevronDown size={16} color={C.textMid} style={{ marginLeft: "auto", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </button>
      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          {body && <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.7 }}>{body}</div>}
          {Array.isArray(items) && items.length > 0 && (
            <div style={{ marginTop: body ? 12 : 0, display: "grid", gap: 9 }}>
              {items.map((it, i) => (
                <div key={i} style={{ fontSize: 13, lineHeight: 1.6, color: C.textMid }}>
                  <span style={{ fontWeight: 800, color: C.text }}>{it.term}. </span>{it.def}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
